import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { backendConfig } from '../config/backendConfig';

export default function MentorOnboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('mentor_session') || '{}'); } catch (_) { return {}; }
  }, []);

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ displayName: '', role: '', years: '', interests: '', bio: '', organization: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    // Bootstrap session from query params if missing
    if ((!session.ownerId || !session.email) && mounted) {
      const sp = new URLSearchParams(window.location.search);
      const qpOwner = (sp.get('ownerId') || '').trim();
      const qpEmail = (sp.get('email') || '').trim();
      const qpCode = (sp.get('code') || '').trim();
      (async () => {
        try {
          if (qpEmail && qpCode) {
            // Validate code to retrieve ownerId and set session
            const resp = await fetch(`${backendConfig.baseUrl}/api/mentors/validate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: qpEmail, code: qpCode })
            });
            const j = await resp.json().catch(()=>({}));
            if (resp.ok && j && j.ownerId) {
              try { localStorage.setItem('mentor_session', JSON.stringify({ ownerId: j.ownerId, email: qpEmail })); } catch (_) {}
              window.location.replace('/mentors/onboarding');
              return;
            }
          } else if (qpOwner && qpEmail) {
            // Direct owner/email bootstrap (dev/admin link)
            try { localStorage.setItem('mentor_session', JSON.stringify({ ownerId: qpOwner, email: qpEmail })); } catch (_) {}
            window.location.replace('/mentors/onboarding');
            return;
          }
        } catch (_) {}
        navigate('/');
      })();
      return;
    }
    
    async function fetchProfile() {
      try {
        const q = new URLSearchParams({ ownerId: session.ownerId || '', email: session.email || '' }).toString();
        const resp = await fetch(`${backendConfig.baseUrl}/api/mentors/profile?${q}`);
        if (resp.ok) {
          const j = await resp.json();
          if (mounted && j.exists && j.profile) {
            setProfile(p => ({ ...p, ...j.profile }));
          }
        }
      } catch (_) {}
      if (mounted) setLoading(false);
    }
    fetchProfile();
    return () => { mounted = false; };
  }, [session.ownerId, session.email, navigate]);

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    setSaving(true);
    
    console.log('Saving mentor profile:', { ownerId: session.ownerId, email: session.email, profile });
    
    try {
      const resp = await fetch(`${backendConfig.baseUrl}/api/mentors/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerId: session.ownerId, email: session.email, profile })
      });
      
      console.log('Profile save response:', resp.status, resp.ok);
      
      if (!resp.ok) {
        const j = await resp.json().catch(()=>({error:'Failed'}));
        console.error('Profile save error:', j);
        setError(j.error || 'Failed to save');
      } else {
        const result = await resp.json().catch(()=>({ok:true}));
        console.log('Profile saved successfully:', result);
        // After successful save, go straight to dashboard
        try { localStorage.setItem('mentorProfileJustSavedAt', String(Date.now())); } catch (_) {}
        navigate('/mentor');
      }
    } catch (e) {
      console.error('Profile save exception:', e);
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
      <div className="text-amber-700">Loading…</div>
    </div>
  );

  // If no valid session, show access denied
  if (!session.ownerId || !session.email) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 shadow-xl border-2 border-amber-200">
            <h2 className="text-2xl font-bold text-amber-800 mb-4">Access Denied</h2>
            <p className="text-amber-700 mb-6">Please log in with your mentor credentials to access this page.</p>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-400 text-white py-3 px-6 rounded-lg hover:from-amber-600 hover:to-amber-500 transition-all duration-200 shadow-md font-medium"
            >
              Go to Artist Corner
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 p-6">
      <form onSubmit={handleSave} className="w-full max-w-2xl bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-amber-200/50">
        <h1 className="text-2xl font-bold text-amber-900 mb-6">Mentor Profile</h1>
        {error && <div className="mb-4 text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">{error}</div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-amber-800 mb-1">Display name</label>
            <input className="w-full px-4 py-3 border border-amber-200 rounded-xl bg-white/80" value={profile.displayName} onChange={e=>setProfile({...profile, displayName:e.target.value})} placeholder="Your name" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-amber-800 mb-1">Role</label>
            <input className="w-full px-4 py-3 border border-amber-200 rounded-xl bg-white/80" value={profile.role} onChange={e=>setProfile({...profile, role:e.target.value})} placeholder="Art teacher / Mentor" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-amber-800 mb-1">Organization</label>
            <input className="w-full px-4 py-3 border border-amber-200 rounded-xl bg-white/80" value={profile.organization} onChange={e=>setProfile({...profile, organization:e.target.value})} placeholder="School / NGO / Institution" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-amber-800 mb-1">Years of experience</label>
            <input className="w-full px-4 py-3 border border-amber-200 rounded-xl bg-white/80" value={profile.years} onChange={e=>setProfile({...profile, years:e.target.value})} placeholder="e.g. 5" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-amber-800 mb-1">Areas of interest</label>
            <input className="w-full px-4 py-3 border border-amber-200 rounded-xl bg-white/80" value={profile.interests} onChange={e=>setProfile({...profile, interests:e.target.value})} placeholder="e.g. Watercolor, Art history" />
          </div>
        </div>
        <div className="mt-6">
          <label className="block text-sm font-semibold text-amber-800 mb-1">Short bio</label>
          <textarea className="w-full px-4 py-3 border border-amber-200 rounded-xl bg-white/80" rows={4} value={profile.bio} onChange={e=>setProfile({...profile, bio:e.target.value})} placeholder="Tell us about your mentoring approach" />
        </div>
        <div className="mt-8">
          <button disabled={saving} className="w-full py-4 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold shadow-lg">{saving ? 'Saving…' : 'Save and continue'}</button>
        </div>
      </form>
    </div>
  );
}


